import { createContext, useContext, useRef, useState, type ReactNode } from 'react';

export type SupportHint = 'import_help' | null;
export type SupportMode = 'help' | 'idea';

export interface SupportOpenOptions {
  mode?: SupportMode;
  message?: string;
}

interface SupportContextValue {
  hint: SupportHint;
  setHint: (hint: SupportHint) => void;
  openSupport: (context?: string, options?: SupportOpenOptions) => void;
  registerOpenHandler: (handler: (context?: string, options?: SupportOpenOptions) => void) => void;
}

const SupportContext = createContext<SupportContextValue | null>(null);

export function SupportProvider({ children }: { children: ReactNode }) {
  const [hint, setHint] = useState<SupportHint>(null);
  const openRef = useRef<(context?: string, options?: SupportOpenOptions) => void>(() => {});

  const openSupport = (context?: string, options?: SupportOpenOptions) => {
    openRef.current(context, options);
  };

  const registerOpenHandler = (handler: (context?: string, options?: SupportOpenOptions) => void) => {
    openRef.current = handler;
  };

  return (
    <SupportContext.Provider value={{ hint, setHint, openSupport, registerOpenHandler }}>
      {children}
    </SupportContext.Provider>
  );
}

export function useSupport() {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error('useSupport must be used within SupportProvider');
  return ctx;
}

export function useSupportOptional() {
  return useContext(SupportContext);
}
